import DeviceLogTypeBadge from '@/components/device-log/device-log-type-badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { IDeviceLog } from '@/entities/device-log.entity';
import type { IClientDeviceLog } from '@/entities/client.entity';
import { cn } from '@/lib/utils';
import { CopyIcon } from 'lucide-react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import { toast } from 'sonner';

const formatter = buildFormatter(russianStrings);

type DeviceLogDetail = IDeviceLog | IClientDeviceLog;

interface DeviceLogDetailDialogProps {
	log: DeviceLogDetail | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	sdkVersion?: string;
}

function formatDateTime(value: string | number) {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
}

function timestampsDiffer(occurredAt: string | number, createdAt: string | number) {
	const diff = Math.abs(new Date(occurredAt).getTime() - new Date(createdAt).getTime());
	return diff > 60_000;
}

export default function DeviceLogDetailDialog({ log, open, onOpenChange, sdkVersion }: DeviceLogDetailDialogProps) {
	if (!log) return null;

	const showBothTimestamps = timestampsDiffer(log.occurredAt, log.createdAt);
	const fullText = [log.message, log.stackTrace].filter(Boolean).join('\n\n');

	function handleCopyAll() {
		navigator.clipboard
			.writeText(fullText)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-[520px] w-full max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0'>
				<DialogHeader className='px-4 pt-4 pb-2 shrink-0'>
					<DialogTitle className='text-left flex flex-wrap items-center gap-2'>
						<DeviceLogTypeBadge logType={log.logType} />
						<span className='text-sm font-normal text-muted-foreground'>
							<TimeAgo date={log.occurredAt} formatter={formatter} />
						</span>
					</DialogTitle>
				</DialogHeader>

				<div className='flex-1 min-h-0 overflow-y-auto px-4 pb-4 flex flex-col gap-3'>
					<div className='grid gap-1 text-sm text-muted-foreground'>
						{'clientId' in log && log.clientId ? <p>Клиент: #{log.clientId}</p> : null}
						<p>Билд: {log.buildId}</p>
						{log.threadName ? <p>Поток: {log.threadName}</p> : null}
						{sdkVersion ? <p>SDK: {sdkVersion}</p> : null}
						<p>
							На устройстве: {formatDateTime(log.occurredAt)}
							{showBothTimestamps ? ` · Сервер: ${formatDateTime(log.createdAt)}` : null}
						</p>
					</div>

					<section className='grid gap-1.5'>
						<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Сообщение</h3>
						<p className='text-sm whitespace-pre-wrap break-words select-text'>{log.message}</p>
					</section>

					{log.stackTrace ? (
						<section className='grid gap-1.5'>
							<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Stack trace</h3>
							<pre
								className={cn(
									'text-xs font-mono whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-2.5',
									'overflow-auto max-h-[50vh] select-text',
								)}
							>
								{log.stackTrace}
							</pre>
						</section>
					) : null}
				</div>

				<div className='shrink-0 border-t border-border px-4 py-3'>
					<Button type='button' variant='secondary' className='w-full' onClick={handleCopyAll}>
						<CopyIcon size={16} />
						Скопировать всё
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
